import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router';
import { PermissionKey } from '@c4ytechnology/authz';
import { AdminListPageHeader, adminDataTableSx, dataTableCardSx } from '../components/admin/adminListPageUi';
import { IamErrorAlert } from '../components/admin/iam/IamErrorAlert';
import { StaffUserPicker, type StaffUserOption } from '../components/admin/iam/StaffUserPicker';
import {
  ADMIN_JIT_ELIGIBILITIES_QUERY_KEY,
  adminIamApi,
  adminJitApi,
  type AdminJitEligibilityListItem,
} from '../api/adminConsole';
import { getAdminConsoleToken } from '../lib/adminConsoleToken';
import { hasAdminPermission } from '../lib/adminPermissions';
import { ADMIN_IAM_ROLES_QUERY_KEY } from '../lib/adminRolesView';
import {
  JIT_DEFAULT_DURATION_MINUTES,
  defaultApproverRoleId,
  durationOptions,
  formatDuration,
  validateEligibilityDraft,
} from '../lib/jitAccess';
import { useAdminConsoleMe } from '../hooks/useAdminConsoleMe';

type Draft = {
  id: string | null;
  user: StaffUserOption | null;
  roleId: string;
  maxDurationMinutes: number;
  requiresApproval: boolean;
  approverRoleId: string;
  reasonRequired: boolean;
};

const EMPTY: Draft = {
  id: null,
  user: null,
  roleId: '',
  maxDurationMinutes: JIT_DEFAULT_DURATION_MINUTES,
  requiresApproval: true,
  approverRoleId: '',
  reasonRequired: true,
};

/**
 * Who may activate which role just in time (RBAC Phase 4 workstream A). Reads need
 * iam.users.read; create / edit / remove need iam.assignments.write and follow the same rules as a
 * grant in admin-api (no self, Super Admin only by a Super Admin, no escalation). Removing an
 * eligibility cancels its pending requests; an activation already in force runs to its expiry.
 */
export function AdminEligibilitiesPage() {
  const theme = useTheme();
  const qc = useQueryClient();
  const hasToken = typeof window !== 'undefined' && !!getAdminConsoleToken();
  const me = useAdminConsoleMe().data;
  const canWrite = hasAdminPermission(me, PermissionKey.IAM_ASSIGNMENTS_WRITE);
  const canReadRoles = hasAdminPermission(me, PermissionKey.IAM_ROLES_READ);

  const listQ = useQuery({
    queryKey: ADMIN_JIT_ELIGIBILITIES_QUERY_KEY,
    queryFn: () => adminJitApi.eligibilities(),
    enabled: hasToken,
  });
  const rolesQ = useQuery({
    queryKey: ADMIN_IAM_ROLES_QUERY_KEY,
    queryFn: () => adminIamApi.roles(),
    enabled: hasToken && canWrite && canReadRoles,
    retry: false,
  });
  const roles = useMemo(() => rolesQ.data?.items ?? [], [rolesQ.data]);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<AdminJitEligibilityListItem | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ADMIN_JIT_ELIGIBILITIES_QUERY_KEY });
  const save = useMutation({
    mutationFn: async (d: Draft) => {
      const v = validateEligibilityDraft({
        userId: d.user?.id ?? '',
        roleId: d.roleId,
        maxDurationMinutes: d.maxDurationMinutes,
        requiresApproval: d.requiresApproval,
        approverRoleId: d.approverRoleId || null,
        reasonRequired: d.reasonRequired,
      });
      if (!v.ok) throw new Error(v.error);
      const { userId, roleId, ...policy } = v.body;
      return d.id ? adminJitApi.updateEligibility(d.id, policy) : adminJitApi.createEligibility({ userId, roleId, ...policy });
    },
    onSuccess: async () => {
      setDraft(null);
      await refresh();
    },
    onError: (err) => {
      if (!(err as { response?: unknown }).response) setDraftError((err as Error).message);
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => adminJitApi.deleteEligibility(id),
    onSuccess: async () => {
      setRemoving(null);
      await refresh();
    },
  });

  if (!hasToken) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">Sign in to view eligibilities.</Alert>
      </Box>
    );
  }

  const open = (d: Draft) => {
    save.reset();
    setDraftError(null);
    setDraft(d);
  };

  const pickRole = (roleId: string) =>
    setDraft((d) => {
      if (!d) return d;
      const key = roles.find((r) => r.id === roleId)?.key ?? '';
      return { ...d, roleId, approverRoleId: defaultApproverRoleId(key, roles) };
    });

  const items = listQ.data?.items ?? [];

  return (
    <Box>
      <AdminListPageHeader
        title="Eligibilities"
        subtitle="Who may activate a critical role just in time, for how long, and who approves. Staff activate from My access."
        endAdornment={
          canWrite && canReadRoles ? (
            <Button variant="contained" onClick={() => open(EMPTY)}>
              Add eligibility
            </Button>
          ) : undefined
        }
      />
      <IamErrorAlert error={listQ.error ?? remove.error} sx={{ mb: 2 }} />
      {canWrite && !canReadRoles && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Adding or editing eligibilities also needs the roles read permission (iam.roles.read).
        </Alert>
      )}

      <Card variant="outlined" sx={dataTableCardSx(theme)}>
        {listQ.isPending ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small" sx={adminDataTableSx(theme)}>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Up to</TableCell>
                <TableCell>Approval</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((e) => (
                <TableRow key={e.id}>
                  <TableCell>
                    <RouterLink to={`/admin/users/${encodeURIComponent(e.userId)}`}>
                      {e.userDisplayName || e.userEmail || e.userId}
                    </RouterLink>
                    {e.userEmail && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {e.userEmail}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{e.roleName ?? e.roleId}</TableCell>
                  <TableCell>{formatDuration(e.maxDurationMinutes)}</TableCell>
                  <TableCell>{e.requiresApproval ? `By ${e.approverRoleName ?? 'approver role'}` : 'Not required'}</TableCell>
                  <TableCell>{e.reasonRequired ? 'Required' : 'Optional'}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {canWrite && canReadRoles && (
                      <Button
                        size="small"
                        onClick={() =>
                          open({
                            id: e.id,
                            user: { id: e.userId, email: e.userEmail ?? e.userId, displayName: e.userDisplayName ?? '' },
                            roleId: e.roleId,
                            maxDurationMinutes: e.maxDurationMinutes,
                            requiresApproval: e.requiresApproval,
                            approverRoleId: e.approverRoleId ?? '',
                            reasonRequired: e.reasonRequired,
                          })
                        }
                      >
                        Edit
                      </Button>
                    )}
                    {canWrite && (
                      <Button size="small" color="error" onClick={() => { remove.reset(); setRemoving(e); }}>
                        Remove
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary">
                      No eligibilities yet. The rollout backfill (rbac:jit-backfill) creates them for today's holders of critical roles.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={Boolean(draft)} onClose={() => setDraft(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{draft?.id ? 'Edit eligibility' : 'Add eligibility'}</DialogTitle>
        <DialogContent dividers>
          {draft && (
            <Stack spacing={2}>
              <IamErrorAlert error={save.error && (save.error as { response?: unknown }).response ? save.error : null} />
              {draftError && <Alert severity="error">{draftError}</Alert>}
              <StaffUserPicker
                label="User"
                value={draft.user}
                disabled={Boolean(draft.id)}
                onChange={(user) => setDraft((d) => (d ? { ...d, user } : d))}
                excludeIds={me?.id ? [me.id] : []}
              />
              <TextField
                select
                size="small"
                label="Role"
                value={draft.roleId}
                disabled={Boolean(draft.id)}
                onChange={(e) => pickRole(e.target.value)}
              >
                {roles.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Maximum duration"
                value={draft.maxDurationMinutes}
                onChange={(e) => setDraft((d) => (d ? { ...d, maxDurationMinutes: Number(e.target.value) } : d))}
              >
                {durationOptions(480).map((m) => (
                  <MenuItem key={m} value={m}>
                    {formatDuration(m)}
                  </MenuItem>
                ))}
              </TextField>
              <FormControlLabel
                control={
                  <Switch
                    checked={draft.requiresApproval}
                    onChange={(e) => setDraft((d) => (d ? { ...d, requiresApproval: e.target.checked } : d))}
                  />
                }
                label="Needs a second person's approval"
              />
              {draft.requiresApproval && (
                <TextField
                  select
                  size="small"
                  label="Approved by holders of"
                  value={draft.approverRoleId}
                  onChange={(e) => setDraft((d) => (d ? { ...d, approverRoleId: e.target.value } : d))}
                  helperText="Anyone who holds or is eligible for this role may approve — never the requester."
                >
                  {roles.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={draft.reasonRequired}
                    onChange={(e) => setDraft((d) => (d ? { ...d, reasonRequired: e.target.checked } : d))}
                  />
                }
                label="A reason is required to activate"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDraft(null)}>Cancel</Button>
          <Button variant="contained" disabled={save.isPending} onClick={() => draft && save.mutate(draft)}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(removing)} onClose={() => setRemoving(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove eligibility</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <IamErrorAlert error={remove.error} />
            <Typography variant="body2">
              {removing?.userEmail ?? removing?.userId} will no longer be able to activate <strong>{removing?.roleName}</strong>.
              Pending requests are cancelled; an activation already in force runs to its expiry.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoving(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={remove.isPending} onClick={() => removing && remove.mutate(removing.id)}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
